import {
  ListenableSubscribable,
  SubscribableSocketV2,
  SubscribableSocketV2Event,
  Subscription,
} from '@benzinga/subscribable';

import {
  FeedId,
  NewsfeedFieldType,
  NewsBZQLQuery,
  NewsBZQLRequestQuery,
  QueryId,
  SocketStory,
  RequiredSocketStoryFields,
} from './entities';
import { SafeError, SafePromise, SafeType, safeResilient } from '@benzinga/safe-await';
import { NewsSession } from './session';
import { Session } from '@benzinga/session';
import { AdvancedNewsEnvironment } from './environment';
import { DateTime } from 'luxon';
import { expressionConvert } from './internal-utils';
import { debounce } from '@benzinga/utils';

/** Kong closes the handshake at 30s. */
const NEWS_SOCKET_CONNECT_TIMEOUT_MS = 30_000;

/** No inbound data. Sits above the server's 60s read/write idle so the server closes first. */
const NEWS_SOCKET_INACTIVITY_MS = 70_000;

/** Server sends a ping every 15s and waits 10s for the pong. */
const NEWS_SOCKET_PING_INTERVAL_MS = 15_000;
const NEWS_SOCKET_PONG_TIMEOUT_MS = 10_000;

/**
 * Backoff ceiling and the time a connection must survive before the ladder resets. The
 * inactivity death window (70s, plus one 10s check) stays under `stableAfterMs` on purpose:
 * a connection killed by the watchdog must still count as a failure, or the flap brake
 * silently disarms and a half-dead endpoint gets hammered.
 */
const NEWS_SOCKET_MAX_RECONNECT_DELAY_MS = 60_000;
const NEWS_SOCKET_STABLE_AFTER_MS = 90_000;

/**
 * Strict pong matcher, matching the hand-rolled heartbeat this replaces: only a real pong
 * frame clears a pending deadline, so a socket that still pushes stories but has stopped
 * answering pings is treated as dead. The substring guard keeps the story firehose from
 * being JSON-parsed twice per message.
 */
const isPongFrame = (msg: string): boolean => {
  if (!msg.includes('"pong"')) {
    return false;
  }
  try {
    return (JSON.parse(msg) as { type?: string }).type === 'pong';
  } catch {
    return false;
  }
};

export interface NewsSocketSubscriptionEvent {
  feedId: string;
  query?: NewsBZQLQuery;
  type: 'news:subscribe';
}
export type NewsContentServerEvent = 'Created' | 'Updated' | 'Removed';

export interface NewsSocketStoryEvent {
  feedIds: string[];
  story: SocketStory;
  type: 'news:subscription_stories';
}

export interface NewsSocketRemovedStoriesEvent {
  feedIds: string[];
  story: SocketStory;
  type: 'news:removed_stories';
}

export const newsSubResultsSocketEventType = (
  event?: NewsContentServerEvent,
): NewsSocketStoryEvent['type'] | NewsSocketRemovedStoriesEvent['type'] =>
  event === 'Removed' ? 'news:removed_stories' : 'news:subscription_stories';

export interface NewsSocketQueryTimeout {
  query?: NewsBZQLQuery;
  queryId: string;
  type: 'news:query_timeout';
}

export interface NewsSocketUnsubscribeEvent {
  feedId: string;
  type: 'news:unsubscribe';
}

export interface NewsSocketQueryEvent {
  query?: NewsBZQLQuery;
  queryId: string;
  stories: SocketStory[];
  type: 'news:query_stories';
}

export interface NewsSocketReconnectEvent {
  type: 'news:reconnected' | 'news:disconnected';
}
export interface NewsSocketSubscriptionErrorEvent extends Required<ErrorResponse> {
  feedIds?: string[];
  responseType: 'news_sub_results' | 'news_sub_confirm' | 'news_unsub_confirm';
  type: 'news:socket_errors';
}

export interface NewsSocketQueryErrorEvent extends Required<ErrorResponse> {
  queryId?: string;
  responseType: 'news_query_resp';
  type: 'news:socket_errors';
}

export type NewsSocketEvent =
  | NewsSocketStoryEvent
  | NewsSocketRemovedStoriesEvent
  | NewsSocketSubscriptionEvent
  | NewsSocketUnsubscribeEvent
  | NewsSocketQueryEvent
  | NewsSocketQueryTimeout
  | NewsSocketSubscriptionErrorEvent
  | NewsSocketQueryErrorEvent
  | NewsSocketReconnectEvent;

export class NewsSocket extends ListenableSubscribable<NewsSocketEvent> {
  private session: Session;
  private socket: SubscribableSocketV2<string, Request>;
  private socketSubscription?: Subscription<SubscribableSocketV2<string, Request>>;

  private feedIdQueryMapping: Map<FeedId, NewsBZQLQuery> = new Map();
  private queryIdQueryMapping: Map<QueryId, NewsBZQLQuery> = new Map();
  private nextQueryId = 0;
  private nextFeedId = 0;

  private state: 'running' | 'stopped' = 'stopped';
  // `stop` is a plain method and `debounce` invokes what it is handed unbound, so it must be
  // wrapped — passing `this.stop` directly throws on `this` inside stop() and the socket is
  // never torn down.
  private debounceStop = debounce(() => this.stop(), 1000);
  private keepSocketAlive: boolean;
  /** True once this socket has opened at least once; see `send` for why that matters. */
  private hasConnected = false;
  /** Whether the current outage has already been announced, so it is announced exactly once. */
  private outageReported = false;

  constructor(session: Session) {
    super();
    this.session = session;
    this.socket = new SubscribableSocketV2<string, Request>(
      session.getEnvironment(AdvancedNewsEnvironment).socketUrl,
      {
        connectTimeoutMs: NEWS_SOCKET_CONNECT_TIMEOUT_MS,
        heartbeat: {
          inactivityMs: NEWS_SOCKET_INACTIVITY_MS,
          ping: {
            intervalMs: NEWS_SOCKET_PING_INTERVAL_MS,
            isPong: isPongFrame,
            makePing: () => ({ type: 'ping' }),
            timeoutMs: NEWS_SOCKET_PONG_TIMEOUT_MS,
          },
        },
        retry: {
          maxDelayMs: NEWS_SOCKET_MAX_RECONNECT_DELAY_MS,
          stableAfterMs: NEWS_SOCKET_STABLE_AFTER_MS,
        },
      },
    );
    this.keepSocketAlive = session.getEnvironment(AdvancedNewsEnvironment).keepSocketAlive === 'true';
    if (this.keepSocketAlive) {
      this.open();
    }
  }

  public query = (data: Partial<NewsBZQLQuery>): SafePromise<SocketStory[]> => {
    const requiredFields: NewsfeedFieldType[] = [
      'CreatedAt',
      'ID',
      'NodeID',
      'UpdatedAt',
      'VersionID',
    ] as RequiredSocketStoryFields;
    const query = {
      fields: data.fields ? Array.from(new Set([...data.fields, ...requiredFields])) : requiredFields,
      limit: data.limit ?? 100,
      offset: data.offset ?? 0,
      sort: data.sort ?? ({ CreatedAt: -1 } as Record<NewsfeedFieldType, -1 | 1>),
      where: data.where ?? null,
    };

    /*
     * One id, one listener and one `queryIdQueryMapping` entry for the whole query, however
     * many times it is retried. Minting a fresh id per attempt left an entry behind for every
     * attempt — only the id that actually got answered was ever removed — and the reconnect
     * handler re-sends every entry it holds, so a query that sat through a few retries came
     * back as that many frames on every later reconnect.
     */
    const queryId = `${this.nextQueryId++}`;
    this.queryIdQueryMapping.set(queryId, query);
    const request: Request = {
      data: { ...query, id: queryId, where: data.where?.toBZQL(expressionConvert(this.session) as any) ?? null },
      type: 'news_query',
    };

    let settle!: (result: SafeType<SocketStory[]>) => void;
    const responded = new Promise<SafeType<SocketStory[]>>(resolve => {
      settle = resolve;
    });
    const subscription = this.subscribe(event => {
      switch (event.type) {
        case 'news:query_stories':
          if (event.queryId === queryId) {
            settle({ ok: event.stories });
          }
          break;
        case 'news:socket_errors':
          if (event.responseType === 'news_query_resp' && event.queryId === queryId) {
            settle({ err: new SafeError(event.errors[0].value, event.errors[0].code, event.errors) });
          }
          break;
      }
    });

    const stories = safeResilient(
      () => {
        // Every attempt re-sends the same frame and waits on the same response.
        this.send(request);
        return responded;
      },
      { delayMultiple: 1.5, delayOffset: 30000, maxDelay: 600000, retryOnError: false },
    )();
    stories.then(() => {
      subscription.unsubscribe();
      this.queryIdQueryMapping.delete(queryId);
    });

    return stories;
  };

  public createFeed = (query: Partial<NewsBZQLQuery>, jumpedToDate?: DateTime): NewsSession => {
    const definedQuery: NewsBZQLQuery = {
      fields: query.fields ?? [],
      limit: query.limit ?? 100,
      offset: query.offset ?? 0,
      sort: query.sort ?? {},
      where: query.where ?? null,
    };

    const feedId = `${this.nextFeedId++}`;
    return new NewsSession(this, definedQuery, feedId, jumpedToDate);
  };

  public startFeed = (newsfeedSession: NewsSession): void => {
    /*
     * `feedIdQueryMapping` answers "what is feed X subscribed to", so it lives and dies with
     * the subscription: set here, released in endFeed. It used to be filled in createFeed and
     * dropped on the FIRST news_sub_confirm, which both leaked — a feed that was created but
     * never subscribed, or never confirmed, stayed in the map for the life of the socket — and
     * went stale, since a feed re-subscribed after a reconnect reported no query at all.
     */
    this.feedIdQueryMapping.set(newsfeedSession.getFeedId(), newsfeedSession.getQuery());
    const { fields, sort, ...query } = newsfeedSession.getQuery();
    const requiredFields: NewsfeedFieldType[] = [
      'CreatedAt',
      'ID',
      'NodeID',
      'UpdatedAt',
      'VersionID',
    ] as RequiredSocketStoryFields;
    this.send({
      data: {
        ...query,
        fields: fields ? Array.from(new Set([...fields, ...requiredFields])) : requiredFields,
        id: newsfeedSession.getFeedId(),
        sort: Object.keys(sort).length === 0 ? undefined : sort,
        where: query.where?.toBZQL(expressionConvert(this.session) as any) ?? null,
      },
      type: 'news_sub',
    });
  };

  public endFeed = (feed: NewsSession): void => {
    const feedId = feed.getFeedId();
    this.feedIdQueryMapping.delete(feedId);
    this.send({ data: { query_ids: [feedId] }, type: 'news_unsub' });
  };

  public getState = (): NewsSocket['state'] => this.state;

  protected onFirstSubscription(): void {
    if (this.keepSocketAlive) {
      return;
    }
    this.open();
  }

  protected onZeroSubscriptions(): void {
    if (this.keepSocketAlive) {
      return;
    }
    this.debounceStop();
  }

  /**
   * Disconnect for good, whoever is still listening. A v2 transport reconnects on its own
   * until it is closed, so dropping the reference without this would leave an unobserved
   * socket reconnecting and pinging the backend forever.
   */
  public close = (): void => {
    this.socket.close();
    this.socketSubscription?.unsubscribe();
    this.socketSubscription = undefined;
    this.state = 'stopped';
    // A later open() is a first connection again: nothing survives to re-issue what is sent
    // while it handshakes.
    this.hasConnected = false;
    this.outageReported = false;
  };

  private stop(): void {
    if (this.hasSubscribers()) return;
    this.close();
  }

  private open = (): void => {
    if (this.socketSubscription === undefined) {
      this.socketSubscription = this.socket.subscribe(this.onMessage);
    }
    this.socket.open();
    this.state = 'running';
  };

  /**
   * Queue only what nobody else will re-issue. Before the first connection there is no
   * reconnect handler to replay a frame, so the transport holds it until the socket opens —
   * that is what lets a freshly opened widget load without waiting on anything. After that,
   * every frame sent during an outage is re-issued by whoever owns it (pending queries here,
   * feed subscriptions by the NewsSession), so the transport must drop rather than replay:
   * doing both would subscribe a feed twice and repeat its stories.
   */
  private send = (request: Request): void => {
    this.socket.sendObject<Request>(request, { policy: this.hasConnected ? 'drop' : 'queue' });
  };

  /** Announce an outage once per cycle, however many attempts it takes to recover. */
  private reportOutage = (): void => {
    if (this.outageReported) {
      return;
    }
    this.outageReported = true;
    this.dispatch({ type: 'news:disconnected' });
  };

  private reportConnected = (): void => {
    this.hasConnected = true;
    this.outageReported = false;
  };

  private processErr = (msg: Response): boolean => {
    if (msg.errors !== undefined) {
      switch (msg.type) {
        case 'news_query_resp': {
          const errors = msg.errors;
          msg.data.matched_queries.forEach(queryId => {
            this.dispatch({ errors, queryId: queryId, responseType: msg.type, type: 'news:socket_errors' });
          });
          break;
        }
        case 'news_sub_results':
          this.dispatch({
            errors: msg.errors,
            feedIds: msg.data?.matched_queries,
            responseType: msg.type,
            type: 'news:socket_errors',
          });
          break;
        case 'news_sub_confirm':
        case 'news_unsub_confirm':
          this.dispatch({
            errors: msg.errors,
            feedIds: [msg.data.id],
            responseType: msg.type,
            type: 'news:socket_errors',
          });
          break;
      }
      return false;
    }
    return true;
  };

  private onMessage = (event: SubscribableSocketV2Event<string>): void => {
    switch (event.type) {
      case 'response': {
        const parsed = JSON.parse(event.msg) as PongResponse | Response;
        if (parsed.type === 'pong') {
          // The transport's heartbeat owns the deadline; a pong carries nothing else.
          break;
        }
        const msg = parsed;
        if (this.processErr(msg)) {
          switch (msg.type) {
            case 'news_sub_confirm':
              this.dispatch({
                feedId: msg.data.id,
                query: this.feedIdQueryMapping.get(msg.data.id),
                type: 'news:subscribe',
              });
              break;
            case 'news_unsub_confirm':
              this.dispatch({ feedId: msg.data.id, type: 'news:unsubscribe' });
              break;
            case 'news_sub_results':
              (msg.data.content ?? []).forEach(story => {
                this.dispatch({
                  feedIds: msg.data.matched_queries,
                  story,
                  type: newsSubResultsSocketEventType(msg.data.event),
                });
              });
              break;
            case 'news_query_resp': {
              const content = msg.data.content ?? [];
              msg.data.matched_queries.forEach(queryId => {
                this.dispatch({
                  query: this.queryIdQueryMapping.get(queryId),
                  queryId: queryId,
                  stories: content,
                  type: 'news:query_stories',
                });
                this.queryIdQueryMapping.delete(queryId);
              });

              break;
            }
          }
        }
        break;
      }
      // First connection of this socket. Anything sent while it handshook was queued by the
      // transport and is being drained right now, so nothing is re-issued here.
      case 'open':
        this.reportConnected();
        break;
      case 'reconnected':
        this.reportConnected();
        this.dispatch({ type: 'news:reconnected' });
        // Every query still awaiting a response was dropped with the old connection; re-issue
        // it on the new one. Feeds are re-subscribed by their NewsSession on the same event.
        this.queryIdQueryMapping.forEach((query, queryId) => {
          this.send({
            data: { ...query, id: queryId, where: query.where?.toBZQL(expressionConvert(this.session) as any) ?? null },
            type: 'news_query',
          });
        });
        break;
      // 'disconnected' opens an outage; 'suspended' (offline or flap cooldown) and 'failed'
      // are outages the transport is not currently retrying out of. All three mean the same
      // thing to the feed: not live.
      case 'disconnected':
      case 'suspended':
      case 'failed':
        this.reportOutage();
        break;
    }
  };
}

interface SubscribeRequest {
  data: NewsBZQLRequestQuery & { id: string };
  type: 'news_sub';
}

interface UnsubscribeRequest {
  data: { query_ids: string[] };
  type: 'news_unsub';
}

interface QueryRequest {
  data: NewsBZQLRequestQuery & { id: string };
  type: 'news_query';
}

interface PingRequest {
  type: 'ping';
}

interface PongResponse {
  result: { code: string; msg: string };
  type: 'pong';
}

type Request = PingRequest | QueryRequest | SubscribeRequest | UnsubscribeRequest;

interface ErrorResponse {
  errors?: { code: string; id: string; value: string }[];
}
interface QueryResponse extends ErrorResponse {
  data: { content: SocketStory[] | null; event?: NewsContentServerEvent; matched_queries: string[] };
  result: { code: string; msg: string };
  type: 'news_query_resp' | 'news_sub_results';
}

interface SubscriptionResponse extends ErrorResponse {
  data: NewsBZQLRequestQuery & { id: string };
  result: { code: string; msg: string };
  type: 'news_sub_confirm';
}

interface UnsubscriptionResponse extends ErrorResponse {
  data: NewsBZQLRequestQuery & { id: string };
  result: { code: string; msg: string };
  type: 'news_unsub_confirm';
}

type Response = QueryResponse | SubscriptionResponse | UnsubscriptionResponse;
